import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, UserPlus, X } from 'lucide-react';
import FloatingHearts from '../components/ui/FloatingHearts';
import { TinyStepsLogo } from '../components/ui/FloatingDecorations';
import SakuraFormBg from '../components/ui/SakuraFormBg';

const LoginPage = () => {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null); // { code, message }
  const [showCreatePopup, setShowCreatePopup] = useState(false);

  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowCreatePopup(false);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError({ code: result.code, message: result.message });
      if (result.code === 'ACCOUNT_NOT_FOUND') setShowCreatePopup(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-5 relative overflow-hidden"
      style={{ background:'linear-gradient(155deg,#FDE8F5 0%,#F5EAFF 40%,#EDE8FF 75%,#FDE8F5 100%)' }}>

      <FloatingHearts density="auth" />

      <div className="relative z-10 w-full max-w-sm">

        {/* Logo */}
        <div className="text-center mb-7">
          <Link to="/" className="inline-flex flex-col items-center gap-2">
            <TinyStepsLogo size={58} />
            <p className="font-display font-bold text-gradient text-xl">TinySteps</p>
          </Link>
          <h1 className="font-display font-bold text-gray-800 text-xl mt-3">Welcome back, Mama</h1>
          <p className="text-gray-500 text-sm font-round mt-1">Sign in to continue your journey</p>
        </div>

        {/* Card */}
        <div className="glass rounded-4xl p-6 shadow-float border border-white/80 animate-scaleIn relative overflow-hidden">
          <SakuraFormBg />
          <div className="relative">

          {/* Error banner */}
          {error && (
            <div className={`rounded-2xl px-4 py-3 mb-5 text-xs font-round border ${
              error.code === 'ACCOUNT_NOT_FOUND'
                ? 'bg-blush-50 border-blush-200 text-blush-600'
                : 'bg-red-50 border-red-200 text-red-500'
            }`}>
              <p className="font-medium">{error.message}</p>
              {error.code === 'ACCOUNT_NOT_FOUND' && (
                <p className="mt-1 opacity-80">
                  New here?{' '}
                  <button type="button"
                    onClick={() => setShowCreatePopup(true)}
                    className="font-bold underline underline-offset-2 hover:opacity-70 transition-opacity">
                    Create your account
                  </button>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 font-round mb-1.5">Email Address</label>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <Mail className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" required autoComplete="email"
                  className="flex-1 px-3 py-3 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 font-round mb-1.5">Password</label>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <Lock className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Your password" required autoComplete="current-password"
                  className="flex-1 px-3 py-3 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="mr-3 flex-shrink-0 text-gray-300 hover:text-blush-400 transition-colors">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full btn-primary py-3.5 text-base mt-2 disabled:opacity-70">
              {loading
                ? <span className="flex items-center justify-center gap-2"><span className="animate-heartbeat">♥</span> Signing in…</span>
                : 'Sign In'}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-blush-100 text-center">
            <p className="text-xs text-gray-400 font-round">
              New to TinySteps?{' '}
              <Link to="/signup" className="text-blush-500 font-bold hover:text-blush-600 transition-colors">
                Create your account →
              </Link>
            </p>
          </div>
          </div>{/* end relative content wrapper */}
        </div>

        <p className="text-center text-[11px] text-gray-400 font-round mt-5">
          Your data is private and secure. Always.
        </p>
      </div>

      {/* "Create Account" popup overlay */}
      {showCreatePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background:'rgba(60,20,60,0.30)', backdropFilter:'blur(6px)' }}
          onClick={() => setShowCreatePopup(false)}>
          <div className="glass rounded-4xl p-7 w-full max-w-xs shadow-float border border-blush-100 animate-scaleIn text-center"
            onClick={e => e.stopPropagation()}>

            <button onClick={() => setShowCreatePopup(false)}
              className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              style={{ position:'absolute' }}>
              <X className="w-3.5 h-3.5" />
            </button>

            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
              style={{ background:'linear-gradient(135deg,#EA86AA,#AF83EC)', boxShadow:'0 4px 14px rgba(246,141,179,0.40)' }}>
              <UserPlus className="w-6 h-6 text-white" />
            </div>

            <h3 className="font-display font-bold text-gray-800 text-lg mb-1">No account yet?</h3>
            <p className="text-gray-500 text-xs font-round mb-6 leading-relaxed">
              We couldn't find a TinySteps account for <strong className="text-blush-500">{email}</strong>.
              Join thousands of mothers on this beautiful journey.
            </p>

            <Link to={`/signup`}
              className="block w-full btn-primary py-3.5 text-sm font-semibold font-round text-center mb-3">
              Create My Account
            </Link>

            <button onClick={() => setShowCreatePopup(false)}
              className="text-xs text-gray-400 font-round hover:text-gray-600 transition-colors">
              I'll try a different email
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
